import React, { useState, useEffect } from 'react';
import { useApi } from '../../../hooks/ApiProvider/ApiProvider';
import styles from './Dashboard.module.css';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';
import { useNavigate } from 'react-router-dom';

// 통계 데이터 타입 정의
interface UserStatistics {
  total_users: number;
  active_users: number;
  inactive_users: number;
}

interface SubscriptionStatistics {
  total_subscription_orders: number;
  completed_subscription_orders: number;
  completed_subscription_amount: number;
}

interface PlanSubscriptionInfo {
  plan_name: string;
  subscriber_count: number;
  total_payment_amount: number;
  percentage: number;
}

interface PlanSubscriptionStatistics {
  total_paid_subscribers: number;
  plan_details: PlanSubscriptionInfo[];
}

interface PhysicalBookOrderStatistics {
  total_orders: number;
  delivery_state_counts: Record<string, number>;
}

interface DashboardStatistics {
  user_statistics: UserStatistics;
  subscription_statistics: SubscriptionStatistics;
  plan_subscription_statistics: PlanSubscriptionStatistics;
  physical_book_order_statistics: PhysicalBookOrderStatistics;
  last_updated: string;
}

// 차트 데이터 타입 정의
interface UserChartData {
  name: string;
  value: number;
  color: string;
}

interface PlanChartData {
  name: string;
  subscribers: number;
  amount: number;
}

interface DeliveryChartData {
  name: string;
  count: number;
}

const Dashboard: React.FC = () => {
  const { getDashboardStatistics } = useApi();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setLoading(true);
        const data = await getDashboardStatistics();
        setStats(data);
        setError(null);
      } catch (err) {
        console.error('대시보드 통계 조회 실패:', err);
        setError('대시보드 통계를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, [getDashboardStatistics]);

  // 차트 데이터 변환 함수들
  const getUserChartData = (userStats: UserStatistics): UserChartData[] => [
    { name: '활성 사용자', value: userStats.active_users, color: '#10b981' },
    { name: '비활성 사용자', value: userStats.inactive_users, color: '#f59e0b' }
  ];

  const getPlanChartData = (planStats: PlanSubscriptionStatistics): PlanChartData[] => 
    planStats.plan_details.map(plan => ({
      name: plan.plan_name,
      subscribers: plan.subscriber_count,
      amount: plan.total_payment_amount
    }));

  const getDeliveryChartData = (deliveryStats: PhysicalBookOrderStatistics): DeliveryChartData[] => 
    Object.entries(deliveryStats.delivery_state_counts).map(([state, count]) => ({
      name: state,
      count: count
    }));

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  if (loading) {
    return (
      <div className={styles.adminDashboard}>
        <div className={styles.dashboardHeader}>
          <h1>관리자 대시보드</h1>
          <p>실시간 데이터를 불러오는 중...</p>
        </div>
        <div className={styles.loading}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
          데이터를 불러오는 중...
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className={styles.adminDashboard}>
        <div className={styles.dashboardHeader}>
          <h1>관리자 대시보드</h1>
          <p>데이터 로딩 중 오류가 발생했습니다</p>
        </div>
        <div className={styles.error}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
          오류: {error || '데이터를 불러올 수 없습니다.'}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.adminDashboard}>
      <div className={styles.dashboardHeader}>
        <h1>관리자 대시보드</h1>
      </div>
      
      {/* 사용자 현황 */}
      <section className={styles.dashboardSection}>
        <div className={styles.sectionHeader}>
          <h2>👥 사용자 현황</h2>
          <button 
            className={styles.sectionLinkButton}
            onClick={() => navigate('/admin/users', {
              state: { from: '/admin/dashboard', fromName: '관리자 대시보드' }
            })}
          >
            사용자 관리 →
          </button>
        </div>
        <div className={styles.dashboardStats}>
          {/* 사용자 분포 파이 차트를 첫 번째 카드로 배치 */}
          <div className={styles.statCard} style={{ marginRight: '1rem' }}>
            <div className={styles.statIcon}>📊</div>
            <h3>사용자 상태별 분포</h3>
            <div className={styles.chartContent}>
              <ResponsiveContainer width="100%" height={120}>
                <PieChart>
                  <Pie
                    data={getUserChartData(stats.user_statistics)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value?.toLocaleString() || 0}명`}
                    outerRadius={45}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {getUserChartData(stats.user_statistics).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => [value.toLocaleString() + '명', '사용자 수']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>👥</div>
            <h3>총 사용자</h3>
            <p className={styles.statNumber}>{stats.user_statistics.total_users.toLocaleString()}</p>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>✅</div>
            <h3>활성 사용자</h3>
            <p className={styles.statNumber}>{stats.user_statistics.active_users.toLocaleString()}</p>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>⏸️</div>
            <h3>비활성 사용자</h3>
            <p className={styles.statNumber}>{stats.user_statistics.inactive_users.toLocaleString()}</p>
          </div>
        </div>
      </section>

      {/* 구독 현황 */}
      <section className={styles.dashboardSection}>
        <div className={styles.sectionHeader}>
          <h2>💳 구독 현황</h2>
          <button 
            className={styles.sectionLinkButton}
            onClick={() => navigate('/admin/orders', {
              state: { from: '/admin/dashboard', fromName: '관리자 대시보드' }
            })}
          >
            결제 관리 →
          </button>
        </div>
        <div className={styles.dashboardStats}>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>📋</div>
            <h3>총 구독 주문</h3>
            <p className={styles.statNumber}>{stats.subscription_statistics.total_subscription_orders.toLocaleString()}</p>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>✅</div>
            <h3>구독 완료</h3>
            <p className={styles.statNumber}>{stats.subscription_statistics.completed_subscription_orders.toLocaleString()}</p>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>💰</div>
            <h3>구독 완료 금액</h3>
            <p className={styles.statNumber}>₩{stats.subscription_statistics.completed_subscription_amount.toLocaleString()}</p>
          </div>
        </div>
        
        {/* 구독 완료율 차트 */}
        <div className={styles.chartContainer}>
          <h3 className={styles.chartTitle}>구독 완료율</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={[
              {
                name: '전체 주문',
                value: stats.subscription_statistics.total_subscription_orders,
                fill: '#e5e7eb'
              },
              {
                name: '완료된 주문',
                value: stats.subscription_statistics.completed_subscription_orders,
                fill: '#10b981'
              }
            ]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value: number) => [value.toLocaleString(), '주문 수']} />
              <Bar dataKey="value" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* 플랜 구독 정보 */}
      <section className={styles.dashboardSection}>
        <div className={styles.sectionHeader}>
          <h2>📊 플랜 구독 정보</h2>
          <button 
            className={styles.sectionLinkButton}
            onClick={() => navigate('/admin/orders', {
              state: { from: '/admin/dashboard', fromName: '관리자 대시보드' }
            })}
          >
            결제 관리 →
          </button>
        </div>
        <div className={styles.dashboardStats}>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>👥</div>
            <h3>총 유료 구독자</h3>
            <p className={styles.statNumber}>{stats.plan_subscription_statistics.total_paid_subscribers.toLocaleString()}</p>
          </div>
        </div>
        
        {/* 플랜별 구독자 수 차트 */}
        <div className={styles.chartContainer}>
          <h3 className={styles.chartTitle}>플랜별 구독자 수</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={getPlanChartData(stats.plan_subscription_statistics)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value: number) => [value.toLocaleString(), '구독자 수']} />
              <Bar dataKey="subscribers" fill="#667eea" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        <div className={styles.planDetails}>
          <h3>플랜별 상세 정보</h3>
          <div className={styles.planList}>
            {stats.plan_subscription_statistics.plan_details.map((plan, index) => (
              <div key={index} className={styles.planItem}>
                <div className={styles.planHeader}>
                  <span className={styles.planName}>{plan.plan_name}</span>
                  <span className={styles.planPercentage}>{plan.percentage}%</span>
                </div>
                <div className={styles.planStats}>
                  <span>구독자: {plan.subscriber_count.toLocaleString()}명</span>
                  <span>총 결제: ₩{plan.total_payment_amount.toLocaleString()}</span>
                </div>
                <div className={styles.planBar}>
                  <div 
                    className={styles.planBarFill} 
                    style={{ width: `${plan.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 실물책 주문 현황 */}
      <section className={styles.dashboardSection}>
        <div className={styles.sectionHeader}>
          <h2>📚 실물책 주문 현황</h2>
          <button 
            className={styles.sectionLinkButton}
            onClick={() => navigate('/admin/delivery', {
              state: { from: '/admin/dashboard', fromName: '관리자 대시보드' }
            })}
          >
            실물책 주문 관리 →
          </button>
        </div>
        <div className={styles.dashboardStats}>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>📦</div>
            <h3>총 주문</h3>
            <p className={styles.statNumber}>{stats.physical_book_order_statistics.total_orders.toLocaleString()}</p>
          </div>
        </div>
        
        {/* 배송 상태별 주문 수 차트 */}
        <div className={styles.chartContainer}>
          <h3 className={styles.chartTitle}>배송 상태별 주문 수</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={getDeliveryChartData(stats.physical_book_order_statistics)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value: number) => [value.toLocaleString(), '주문 수']} />
              <Bar 
                dataKey="count" 
                fill="#667eea"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        <div className={styles.deliveryStates}>
          <h3>배송 상태별 주문 수</h3>
          <div className={styles.deliveryList}>
            {Object.entries(stats.physical_book_order_statistics.delivery_state_counts).map(([state, count]) => (
              <div key={state} className={styles.deliveryItem}>
                <span className={styles.deliveryState}>{state}</span>
                <span className={styles.deliveryCount}>{count.toLocaleString()}건</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className={styles.lastUpdated}>
        <span style={{ marginRight: '0.5rem' }}>🕒</span>
        마지막 업데이트: {new Date(stats.last_updated).toLocaleString('ko-KR')}
      </div>
    </div>
  );
};

export default Dashboard;
